import { useMemo, useState } from "react";
import { DEPARTEMENTS, rechercherDepartements } from "@shared/departements";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

interface Props {
  value: string[];
  onChange: (codes: string[]) => void;
  disabled?: boolean;
}

export default function DepartementsSelector({ value, onChange, disabled }: Props) {
  const [q, setQ] = useState("");
  const selected = new Set(value);

  const filtered = useMemo(() => rechercherDepartements(q), [q]);

  function toggle(code: string) {
    if (disabled) return;
    const next = new Set(selected);
    next.has(code) ? next.delete(code) : next.add(code);
    onChange([...next].sort());
  }

  return (
    <div className="space-y-2">
      {/* Chips */}
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {value.length === 0 ? (
          <span className="text-xs text-muted-foreground">Aucun département sélectionné</span>
        ) : (
          value.map((code) => {
            const d = DEPARTEMENTS.find((x) => x.code === code);
            return (
              <Badge
                key={code}
                variant="secondary"
                className="gap-1 pr-1 text-xs font-normal"
              >
                <span className="font-semibold">{code}</span> · {d?.nom}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => toggle(code)}
                    className="ml-0.5 rounded-full hover:bg-muted/60 p-0.5"
                    aria-label={`Retirer ${d?.nom}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                )}
              </Badge>
            );
          })
        )}
      </div>

      {/* Search */}
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Rechercher un département (code ou nom)…"
        className="h-8 text-sm"
        disabled={disabled}
      />

      {/* Scrollable list */}
      <div className="max-h-48 overflow-y-auto border rounded-lg divide-y divide-border/50">
        {filtered.map((d) => (
          <label
            key={d.code}
            className={`flex items-center gap-2.5 px-3 py-1.5 cursor-pointer text-sm hover:bg-accent/50 transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <input
              type="checkbox"
              checked={selected.has(d.code)}
              onChange={() => toggle(d.code)}
              disabled={disabled}
              className="accent-blue-600"
            />
            <span className="w-8 text-muted-foreground text-xs font-mono">{d.code}</span>
            <span>{d.nom}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
